function makeProxyClone(original, proxyPid, proxyId) {
  const clone = JSON.parse(JSON.stringify(original));

  clone.pid = proxyPid;
  clone.uid = `${original.uid || original.pid}-proxy-${proxyId}`;
  clone.isProxy = true;
  clone.proxyOf = original.pid;

  clone.spouseIds = [proxyId];

  clone.motherId = null;
  clone.fatherId = null;
  clone.childrenIds = [];
  clone.siblingIds = [];

  delete clone.mother;
  delete clone.father;
  delete clone.children;
  delete clone.siblingType;
  delete clone.twins;
  delete clone.relationship;
  delete clone.spouse;

  // The original's marriage may carry a Set-Relation cross-level marker
  // (crossLevelSpouseIds) — an EXPLICIT diagonal. The clone exists precisely to
  // redraw that marriage LEVEL on the spouse's generation, so it must not
  // inherit the marker or generation assignment will refuse to seat it beside
  // its spouse (G-1-01411).
  delete clone.crossLevelSpouseIds;

  return clone;
}

export function deriveProxyNodes(records, proxies) {
  if (
    !Array.isArray(records) ||
    !Array.isArray(proxies) ||
    proxies.length === 0
  ) {
    return records || [];
  }

  const working = records.slice();

  // Synthetic proxy pids start above the highest real pid so they never collide.
  let maxPid = 0;
  for (const r of working) {
    const n = Number(r?.pid);
    if (!Number.isNaN(n)) maxPid = Math.max(maxPid, n);
  }
  let nextProxyPid = maxPid + 1;

  for (const intent of proxies) {
    const originalPid = intent?.originalPid;
    const proxyId = intent?.proxyId;
    if (originalPid == null || proxyId == null) continue;

    const idxO = working.findIndex((p) => p.pid === originalPid);
    const idxS = working.findIndex((p) => p.pid === proxyId);
    if (idxO < 0 || idxS < 0) continue;

    const original = working[idxO];
    // Only derive when they are genuinely still spouses.
    if (!(original.spouseIds || []).includes(proxyId)) continue;

    const proxyPid = nextProxyPid++;

    // Children shared by the original person and this spouse — the same rule the
    // old persisted-proxy path used to decide which children move to the proxy.
    const sharedChildPids = (original.childrenIds || []).filter((childPid) => {
      const child = working.find((p) => p.pid === childPid);
      if (!child) return false;
      return (
        (child.motherId === originalPid && child.fatherId === proxyId) ||
        (child.fatherId === originalPid && child.motherId === proxyId)
      );
    });

    const newOriginal = { ...original };
    newOriginal.spouseIds = (original.spouseIds || []).filter(
      (id) => id !== proxyId
    );
    newOriginal.childrenIds = (original.childrenIds || []).filter(
      (id) => !sharedChildPids.includes(id)
    );
    working[idxO] = newOriginal;

    const spouse = working[idxS];
    const newSpouse = { ...spouse };
    newSpouse.spouseIds = Array.from(
      new Set([
        ...(spouse.spouseIds || []).filter((id) => id !== originalPid),
        proxyPid
      ])
    );
    working[idxS] = newSpouse;

    for (const childPid of sharedChildPids) {
      const cIdx = working.findIndex((p) => p.pid === childPid);
      if (cIdx < 0) continue;
      const child = { ...working[cIdx] };
      if (child.motherId === originalPid) child.motherId = proxyPid;
      if (child.fatherId === originalPid) child.fatherId = proxyPid;
      working[cIdx] = child;
    }

    const proxy = makeProxyClone(original, proxyPid, proxyId);
    proxy.childrenIds = sharedChildPids.slice();
    working.push(proxy);
  }

  return working;
}
